import { cn } from '@repo/tailwind-config/tailwind.functions'
import { colorsMapping } from '@repo/toolkit/colors'
import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import { StringFormatter } from '@repo/toolkit/string'
import Badge from '@repo/ui/badge'
import Checkbox from '@repo/ui/checkbox'
import { Column } from '@repo/ui/data-table'
import { DebouncedInput } from '@repo/ui/debounced-input'
import { BagIcon, CreditCardIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'

import { reportStatusEnumMapping } from '../../../lib/types/enums/affected-staff/report-status.enum'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { AffectedStaffMap } from '../../../lib/types/models/affectedStaff/affected-staff'
import { StaffRequestProperties } from '../../../lib/types/models/staff-request/properties.staff-request'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { getIbanBadgeColors } from '../../../utils/functions/get-iban-badge-color'
import CountryFlag from '../../elements/country-flag'
import PeriodBadge from '../../elements/period-badge'
import { QualificationBadge } from '../../elements/qualification-badge'
import SalaryPaymentStatus from '../../elements/salary-payment-status'
import { createAffectedStaffSeniorityColumn } from './affected-staff-seniority-column'

type ColumnExtraAttributes = {
  version: RoleEnum[]
}

type AffectedStaffSalaryColumn = Column<AffectedStaffMap['SALARY']> & ColumnExtraAttributes

// Lets a badge grow to several lines in the wrapping columns, keeping the default height on one line
const wrappingBadgeClassName = 'h-auto min-h-7 py-1'

const affectedStaffSalaryColumn = ({
  handlePasBlockedChange,
  handlePasChange
}: {
  handlePasBlockedChange?: (checked: boolean, affectedStaff: AffectedStaffMap['SALARY']) => void
  handlePasChange?: (pas: number | null, affectedStaff: AffectedStaffMap['SALARY']) => void
}): AffectedStaffSalaryColumn[] => [
  {
    header: '#',
    accessorKey: 'id',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row, table }) => {
      const { pageIndex, pageSize } = table.getState().pagination
      return pageIndex * pageSize + row.index + 1
    }
  },
  {
    header: 'Nom',
    accessorKey: 'staff.lastName',
    enableSorting: true,
    cellClassName: 'whitespace-normal',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => row.original.staff.lastName.toUpperCase()
  },
  {
    header: 'Prénom',
    accessorKey: 'staff.firstName',
    enableSorting: true,
    cellClassName: 'whitespace-normal',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => StringFormatter.capitalize(row.original.staff.firstName)
  },
  {
    header: 'Qualification',
    accessorKey: 'staffRequest.qualification',
    cellClassName: 'whitespace-normal',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => (
      <QualificationBadge
        qualification={row.original.staffRequest.qualification}
        className={wrappingBadgeClassName}
      />
    )
  },
  {
    header: 'Pays',
    accessorKey: 'staffRequest.country.name',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => {
      const countryCode = row.original.staffRequest.country?.code
      if (!countryCode) return '-'
      return <CountryFlag code={countryCode} />
    }
  },
  {
    header: 'Centres',
    accessorKey: 'staffRequest.centers',
    cellClassName: 'whitespace-normal',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => (
      <div className="flex flex-col gap-1">
        {row.original.staffRequest.centers.map((center) => (
          <Typography key={center.id} variant="secondary-body">
            {center.name}
          </Typography>
        ))}
      </div>
    )
  },
  {
    header: 'Date',
    accessorKey: 'staffRequest.startDate',
    enableSorting: true,
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => {
      const { startDate, endDate } = row.original.staffRequest
      if (!startDate || !endDate) return '-'
      const text = (
        <div className="flex flex-col">
          <span>{DateFormatter.DDMMYYYY(startDate)}</span>
          <span>{DateFormatter.DDMMYYYY(endDate)}</span>
        </div>
      )
      const period = StaffRequestProperties.getPeriod(row.original.staffRequest)
      if (!period) return <Badge text={text} />
      return <PeriodBadge period={period} text={text} />
    }
  },
  {
    header: 'Durée',
    accessorKey: 'staffRequest.duration',
    enableSorting: true,
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => {
      const duration = row.original.staffRequest.duration
      if (!duration) return '-'
      return <Badge text={`${duration.toString()} jours`} />
    }
  },
  {
    header: 'Formules',
    accessorKey: 'staffRequest.formulas.title',
    cellClassName: 'whitespace-normal',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => (
      <div className="flex flex-col">
        {row.original.staffRequest.formulas.length > 0
          ? row.original.staffRequest.formulas.map((formula) => (
              <span key={formula.id}>{getTranslatedName(formula)}</span>
            ))
          : '-'}
      </div>
    )
  },
  createAffectedStaffSeniorityColumn<AffectedStaffMap['SALARY'], ColumnExtraAttributes>({
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN]
  }),
  {
    header: 'Honoraires',
    accessorKey: 'originalBaseSalary',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row: { original: affectedStaff } }) => (
      <Badge
        variant="primary"
        text={NumberFormatter.formatCurrency(
          affectedStaff.effectiveBaseSalary ?? 0,
          affectedStaff.currency
        )}
      />
    )
  },
  {
    header: 'Prime',
    accessorKey: 'applicableBonusPayscale',
    cellClassName: 'whitespace-normal',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => {
      const { applicableBonusPayscale: payscale, appliedBonus } = row.original
      if (!payscale || appliedBonus === null) return '-'
      return (
        <Badge
          variant="primary"
          className={wrappingBadgeClassName}
          text={`${payscale.name} - ${NumberFormatter.formatCurrency(appliedBonus, row.original.currency)}`}
        />
      )
    }
  },
  {
    header: 'Prime exceptionnelle',
    accessorKey: 'exceptionalBonusAmount',
    cellClassName: 'whitespace-normal',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => {
      const exceptionalBonusAmount = row.original.exceptionalBonusAmount
      const reason = row.original.exceptionalBonusReason
      if (!exceptionalBonusAmount) return '-'
      if (reason)
        return (
          <Badge
            variant="primary"
            className={wrappingBadgeClassName}
            text={`${reason} - ${NumberFormatter.formatCurrency(exceptionalBonusAmount, row.original.currency)}`}
          />
        )

      return (
        <Badge
          variant="primary"
          text={NumberFormatter.formatCurrency(exceptionalBonusAmount, row.original.currency)}
        />
      )
    }
  },
  {
    header: 'Salaire net\navant PAS',
    accessorKey: 'totalSalary',
    columnHeaderClassName: 'whitespace-pre-line',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => (
      <Badge
        variant="primary"
        text={NumberFormatter.formatCurrency(row.original.totalSalary ?? 0, row.original.currency)}
      />
    )
  },
  {
    header: 'Montant du PAS',
    accessorKey: 'pas',
    version: [RoleEnum.ACCOUNTANT],
    cell: ({ row }) => {
      const { pas, pasBlocked, id } = row.original
      if (pasBlocked)
        return pas != null ? (
          <Badge
            variant="primary"
            text={NumberFormatter.formatCurrency(pas, row.original.currency)}
          />
        ) : (
          '-'
        )
      return (
        <DebouncedInput
          key={`pas-${id}-${pas}`}
          label="PAS"
          type="number"
          decimalPlaces={2}
          value={pas}
          onChange={(e) =>
            handlePasChange?.(e.target.value === '' ? null : Number(e.target.value), row.original)
          }
        />
      )
    }
  },
  {
    header: 'Montant du PAS',
    accessorKey: 'pas',
    version: [RoleEnum.ADMIN],
    cell: ({ row }) =>
      row.original.pas != null ? (
        <Badge
          variant="primary"
          text={NumberFormatter.formatCurrency(row.original.pas, row.original.currency)}
        />
      ) : (
        '-'
      )
  },
  {
    header: 'Salaire net\naprès PAS',
    accessorKey: 'viewData.totalSalaryAfterPas',
    columnHeaderClassName: 'whitespace-pre-line',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => (
      <Badge
        variant="primary"
        text={NumberFormatter.formatCurrency(
          row.original.viewData.totalSalaryAfterPas,
          row.original.currency
        )}
      />
    )
  },
  {
    header: 'Ok',
    accessorKey: 'pasBlocked',
    version: [RoleEnum.ADMIN],
    cell: ({ row }) => (
      <Checkbox
        checked={row.original.pasBlocked}
        onChange={(checked) => handlePasBlockedChange?.(checked, row.original)}
      />
    )
  },
  {
    header: 'Sacoche/\nrapport',
    accessorKey: 'endOfStayReportStatus',
    columnHeaderClassName: 'whitespace-pre-line',
    version: [RoleEnum.ADMIN],
    cell: ({ row }) => {
      const { endOfStayReportStatus } = row.original
      const col = colorsMapping[reportStatusEnumMapping[endOfStayReportStatus].variant]

      return (
        <Badge
          icon={<BagIcon color={col.text} />}
          iconPosition="start"
          variant={reportStatusEnumMapping[endOfStayReportStatus].variant}
          size="flexible"
          className="rounded"
        />
      )
    }
  },
  {
    header: 'Banque',
    accessorKey: 'staff.ibanDetailsFilled',
    version: [RoleEnum.ADMIN],
    cell: ({ row }) => {
      const { ibanDetailsFilled, ibanExportedAt } = row.original.staff
      const { color, bgColor } = getIbanBadgeColors(ibanDetailsFilled, ibanExportedAt)
      return (
        <Badge
          icon={<CreditCardIcon color={color} />}
          iconPosition="start"
          color={bgColor}
          size="flexible"
          className="rounded"
        />
      )
    }
  },
  {
    header: 'Statut du paiement',
    accessorKey: 'paymentStatus',
    cellClassName: 'whitespace-normal',
    version: [RoleEnum.ADMIN],
    cell: ({ row }) => {
      const { paymentStatus, paymentValidatedAt, paymentExportedAt } = row.original
      return (
        <SalaryPaymentStatus
          paymentStatus={paymentStatus}
          paymentValidatedAt={paymentValidatedAt}
          paymentExportedAt={paymentExportedAt}
        />
      )
    }
  }
]

type GetAffectedStaffSalaryColumnsParams = {
  role: RoleEnum
  handlePasBlockedChange?: (checked: boolean, affectedStaff: AffectedStaffMap['SALARY']) => void
  handlePasChange?: (pas: number | null, affectedStaff: AffectedStaffMap['SALARY']) => void
}

export function getAffectedStaffSalaryColumns(params: GetAffectedStaffSalaryColumnsParams) {
  return affectedStaffSalaryColumn({
    handlePasBlockedChange: params.handlePasBlockedChange,
    handlePasChange: params.handlePasChange
  })
    .filter((column) => column.version.includes(params.role))
    .map((column) => ({
      ...column,
      // Tighter spacing so the whole table fits a 23-inch screen
      columnHeaderClassName: cn('pl-1', column.columnHeaderClassName),
      cellClassName: cn('pl-1', column.cellClassName)
    }))
}
